import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ChevronLeft, Activity, Link2 } from 'lucide-react'
import { Button, Tooltip, buttonVariants, cn } from '@ludus/ui'
import type { InternalStatus, Lead, PipelineId } from '@ludus/core'
import { MoveStageDialog } from './MoveStageDialog.tsx'

interface LeadDetailHeaderProps {
  lead: Lead
  statuses: InternalStatus[]
  pipelineId: PipelineId
  onSaved: (lead: Lead) => void
}

/**
 * Hero-zone header: lead title on the left, action group on the right.
 * Breadcrumb lives above the hero section (rendered by `LeadDetailPage`)
 * so the hero card stays focused on the deal itself.
 */
export function LeadDetailHeader({ lead, statuses, pipelineId, onSaved }: LeadDetailHeaderProps) {
  const { t } = useTranslation()
  const comingSoon = t('pages.leads.detail.actions.comingSoon', {
    defaultValue: 'Coming in a later milestone',
  })

  return (
    // `basis` sets when the row breaks — a long title alone used to break it
    // on a 1600px screen — and `ml-auto` keeps the actions right once it does.
    <div className="flex flex-wrap items-center gap-3">
      <h1 className="min-w-0 flex-1 basis-80 text-xl font-semibold tracking-tight text-balance text-foreground sm:text-2xl">
        {lead.name}
      </h1>
      <div className="ml-auto flex flex-wrap items-center gap-2">
        {/* Only when the card is actually on a cycle. `ludusLeadId` is null for
            every ingested card today (#254), so this is absent rather than a
            dead link to a sale that does not exist. */}
        {lead.ludusLeadId != null && (
          <Link
            to={`/sales/${lead.ludusLeadId}`}
            className={cn(
              buttonVariants({ variant: 'ghost', size: 'sm' }),
              'inline-flex items-center gap-1.5',
            )}
          >
            <Link2 className="h-3.5 w-3.5" aria-hidden="true" />
            <span className="hidden sm:inline">
              {t('pages.leads.detail.sale', { defaultValue: 'The whole sale' })}
            </span>
          </Link>
        )}
        <Link
          to={`/pipelines/${lead.pipelineId}`}
          aria-label={t('pages.leads.detail.back', { defaultValue: 'Back to pipeline' })}
          className={cn(
            buttonVariants({ variant: 'ghost', size: 'sm' }),
            'inline-flex items-center gap-1.5',
          )}
        >
          <ChevronLeft className="h-3.5 w-3.5" aria-hidden="true" />
          {/* Icon-only on mobile to free row width for the action buttons. */}
          <span className="hidden sm:inline">
            {t('pages.leads.detail.back', { defaultValue: 'Back to pipeline' })}
          </span>
        </Link>
        <Tooltip content={comingSoon}>
          <Button variant="outline" size="sm" disabled>
            <Activity className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
            {t('pages.leads.detail.actions.logActivity', { defaultValue: 'Log activity' })}
          </Button>
        </Tooltip>
        <MoveStageDialog
          lead={lead}
          statuses={statuses}
          pipelineId={pipelineId}
          onSaved={onSaved}
        />
      </div>
    </div>
  )
}
